export type AssetVariant = 'original' | 'thumbnail' | 'preview';
export const imageTransform = (variant: AssetVariant) => variant === 'original' ? undefined :
  variant === 'thumbnail' ? { width: 640, height: 480, resize: 'contain' as const, quality: 70 } :
    { width: 1600, height: 1600, resize: 'contain' as const, quality: 85 };

// Memory only. Auth changes invalidate pending work as well as resolved URLs.
export function createAssetCache(now = () => Date.now()) {
  let generation = 0;
  const entries = new Map<string, { url: string; expires: number }>();
  const pending = new Map<string, Promise<string>>();
  const clear = () => { generation++; entries.clear(); pending.clear(); };
  const resolve = (scope: string, asset: string, variant: AssetVariant, sign: () => Promise<string>) => {
    const key = JSON.stringify([scope, asset, variant]);
    const cached = entries.get(key);
    if (cached && cached.expires > now()) return Promise.resolve(cached.url);
    const running = pending.get(key);
    if (running) return running;
    const epoch = generation;
    const started = now();
    const request = Promise.resolve().then(sign).then(url => {
      if (epoch !== generation) throw new Error('Session changed');
      // Conservative TTL: signer issues five-minute URLs; reserve thirty seconds.
      entries.set(key, { url, expires: started + 270000 });
      if (entries.size > 200) entries.delete(entries.keys().next().value as string);
      return url;
    }).finally(() => { if (pending.get(key) === request) pending.delete(key); });
    pending.set(key, request);
    return request;
  };
  const remaining = (scope: string, asset: string, variant: AssetVariant) => Math.max(0, (entries.get(JSON.stringify([scope, asset, variant]))?.expires || 0) - now());
  const discard = (asset: string) => { for (const key of Array.from(entries.keys())) { if (JSON.parse(key)[1] === asset) entries.delete(key); } };
  return { resolve, clear, remaining, discard };
}
